import {
  Controller,
  Control,
  FieldErrors,
  UseFormWatch,
} from 'react-hook-form';
import { LoanFormData } from '@/schemas/loanSchema';
import { PropertyLocation } from '@/types/loan';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface Props {
  control: Control<LoanFormData>;
  errors: FieldErrors<LoanFormData>;
  watch: UseFormWatch<LoanFormData>;
  addPropertyLocation: () => void;
  removePropertyLocation: (index: number) => void;
}

export const PropertyLocationsSection = ({
  control,
  watch,
  addPropertyLocation,
  removePropertyLocation,
}: Props) => {
  const locations = watch('property_locations') || [];
  return (
    <div className="p-8 mb-8" data-test-id="property-locations-container">
      <h3 className="section-title" data-test-id="property-locations-section">
        Property Locations
      </h3>
      {locations.map((_: PropertyLocation, idx: number) => (
        <div
          key={idx}
          className="group-box"
          data-test-id={`property-location-${idx}-container`}
        >
          <div
            className="grid-3"
            data-test-id={`property-location-${idx}-grid`}
          >
            <div data-test-id={`street-${idx}-field`}>
              <Label
                className="form-label loanform-label"
                htmlFor={`property_locations.${idx}.street`}
                data-test-id={`street-${idx}-label`}
              >
                Street Address *
              </Label>
              <Controller
                name={`property_locations.${idx}.street`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={`property_locations.${idx}.street`}
                    className="input-full loanform-input loanform-input-bg loanform-input-border"
                    value={field.value ?? ''}
                    data-test-id={`street-${idx}-input`}
                  />
                )}
              />
            </div>
            <div data-test-id={`city-${idx}-field`}>
              <Label
                className="form-label loanform-label"
                htmlFor={`property_locations.${idx}.city`}
                data-test-id={`city-${idx}-label`}
              >
                City *
              </Label>
              <Controller
                name={`property_locations.${idx}.city`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={`property_locations.${idx}.city`}
                    className="input-full loanform-input loanform-input-bg loanform-input-border"
                    value={field.value ?? ''}
                    data-test-id={`city-${idx}-input`}
                  />
                )}
              />
            </div>
            <div data-test-id={`state-${idx}-field`}>
              <Label
                className="form-label loanform-label"
                htmlFor={`property_locations.${idx}.state`}
                data-test-id={`state-${idx}-label`}
              >
                State *
              </Label>
              <Controller
                name={`property_locations.${idx}.state`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={`property_locations.${idx}.state`}
                    className="input-full loanform-input loanform-input-bg loanform-input-border"
                    value={field.value ?? ''}
                    data-test-id={`state-${idx}-input`}
                  />
                )}
              />
            </div>
            <div data-test-id={`zip-code-${idx}-field`}>
              <Label
                className="form-label loanform-label"
                htmlFor={`property_locations.${idx}.zip_code`}
                data-test-id={`zip-code-${idx}-label`}
              >
                Zip Code
              </Label>
              <Controller
                name={`property_locations.${idx}.zip_code`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={`property_locations.${idx}.zip_code`}
                    className="input-full loanform-input loanform-input-bg loanform-input-border"
                    value={field.value ?? ''}
                    data-test-id={`zip-code-${idx}-input`}
                  />
                )}
              />
            </div>
            <div data-test-id={`country-${idx}-field`}>
              <Label
                className="form-label loanform-label"
                htmlFor={`property_locations.${idx}.country`}
                data-test-id={`country-${idx}-label`}
              >
                Country *
              </Label>
              <Controller
                name={`property_locations.${idx}.country`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={`property_locations.${idx}.country`}
                    className="input-full loanform-input loanform-input-bg loanform-input-border"
                    value={field.value ?? ''}
                    data-test-id={`country-${idx}-input`}
                  />
                )}
              />
            </div>
            <div data-test-id={`region-${idx}-field`}>
              <Label
                className="form-label loanform-label"
                htmlFor={`property_locations.${idx}.region`}
                data-test-id={`region-${idx}-label`}
              >
                Region *
              </Label>
              <Controller
                name={`property_locations.${idx}.region`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={`property_locations.${idx}.region`}
                    className="input-full loanform-input loanform-input-bg loanform-input-border"
                    value={field.value ?? ''}
                    data-test-id={`region-${idx}-input`}
                  />
                )}
              />
            </div>
          </div>
          <div
            className="group-actions"
            data-test-id={`property-location-${idx}-actions`}
          >
            <Button
              type="button"
              variant="secondary"
              onClick={() => removePropertyLocation(idx)}
              disabled={locations.length === 1}
              data-test-id={`remove-location-${idx}-button`}
            >
              Remove
            </Button>
            {idx === locations.length - 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={addPropertyLocation}
                data-test-id="add-location-button"
              >
                Add Location
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
